/**
 * Intelligent Form Triage — the one classification every Form Entry gets.
 *
 * @shared-compat: lives beside form-intake.ts and intake-projection.ts because
 * the same three values are written by the ingest Worker, read and corrected by
 * the portal API, and rendered by the shared portal UI. A second copy of this
 * vocabulary in any of those places is how the three surfaces drift apart.
 *
 * The message and the intent it expresses are the decision. Phone shape, area
 * code, name, and general plausibility may support a call, but a nonlocal
 * number never demotes an otherwise legitimate request — out-of-area customers
 * are customers, and the same holds for the language a request is written in.
 *
 * Only Low Value is filtered out of a human's view, which is why the vocabulary
 * separates `other_business` (billing, employment, an existing job — real mail
 * with no route of its own yet) from `low_value` (unsolicited traffic). Folding
 * the two together would hide a job applicant behind a spam filter.
 *
 * There is no retry path on purpose: an unavailable or unparseable
 * classification lands on Needs Review, which is exactly where a human should
 * look anyway.
 */

export const FORM_TRIAGE_CLASSIFICATIONS = [
  'likely_service_request',
  'other_business',
  'needs_review',
  'low_value',
] as const;

export type FormTriageClassification = (typeof FORM_TRIAGE_CLASSIFICATIONS)[number];

export const FORM_TRIAGE_FALLBACK: FormTriageClassification = 'needs_review';

/**
 * Classifications that belong in the actionable Form Entry feed.
 *
 * Everything except Low Value. Other Business is real mail from a real person —
 * billing, employment, an existing job — and there is no other route for it yet,
 * so it stays in front of a human rather than being filed away unread.
 */
export const ACTIONABLE_FORM_TRIAGE_CLASSIFICATIONS: FormTriageClassification[] = [
  'likely_service_request',
  'other_business',
  'needs_review',
];

const LABELS: Record<FormTriageClassification, string> = {
  likely_service_request: 'Likely Service Request',
  other_business: 'Other Business',
  needs_review: 'Needs Review',
  low_value: 'Low Value',
};

/**
 * The LeadLoop gate for a Form Entry — the counterpart to isLeadLoopEligibleCall.
 *
 * Fail-open on purpose: an unclassified or unrecognized value keeps the entry in
 * the queue. Only an explicit Low Value verdict removes work from a human's view.
 */
export function isLeadLoopEligibleFormTriage(value: unknown): boolean {
  return formTriageClassification(value) !== 'low_value';
}

export function formTriageClassificationLabel(value: string | null | undefined): string {
  return LABELS[formTriageClassification(value) ?? FORM_TRIAGE_FALLBACK];
}

export function formTriageClassification(
  value: unknown,
): FormTriageClassification | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_') as FormTriageClassification;
  return FORM_TRIAGE_CLASSIFICATIONS.includes(normalized) ? normalized : undefined;
}

export interface FormTriageInput {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  message?: string | null;
}

export interface FormTriageResult {
  classification: FormTriageClassification;
  explanation: string;
  classifiedAt: string;
}

export const FORM_TRIAGE_MAX_EXPLANATION_CHARS = 400;

export const FORM_TRIAGE_SCHEMA = {
  name: 'form_entry_triage',
  strict: true,
  schema: {
    type: 'object',
    additionalProperties: false,
    required: ['classification', 'explanation'],
    properties: {
      classification: {
        type: 'string',
        enum: [...FORM_TRIAGE_CLASSIFICATIONS],
        description: 'Exactly one classification for this submission.',
      },
      explanation: {
        type: 'string',
        description: 'One short sentence naming the evidence behind the classification.',
      },
    },
  },
} as const;

const SYSTEM_PROMPT = [
  'Classify one website form submission to a service business (electrical contracting) into exactly one of four values.',
  '',
  'likely_service_request — the submission clearly asks for service, a quote, an estimate,',
  'an appointment, or help with a project.',
  'other_business — a real person contacting the business about something other than new work:',
  'billing or an invoice, an existing job or warranty, scheduling, employment or a job application,',
  'a supplier or subcontractor the business already works with, or a general question to the office.',
  'needs_review — the submission could be legitimate but the intent is unclear.',
  'low_value — the submission is unsolicited or worthless traffic: spam, scams, cold sales and',
  'marketing pitches, SEO or link-building offers, agency and lead-generation outreach, bulk',
  'promotion, gibberish, or a message with no discernible business purpose.',
  '',
  'The message and the intent it expresses are the strongest signals. Name, phone validity,',
  'area code, and general human plausibility may support a decision but never decide it alone.',
  'A nonlocal or unusual phone number must NOT make an otherwise legitimate request low_value.',
  'A short or blunt request for work is still a service request.',
  'The language a submission is written in is NOT a signal by itself: a genuine request for',
  'service or a genuine business question stays likely_service_request or other_business no',
  'matter what language it is written in. Only classify a foreign-language message low_value',
  'when its own content is spam or promotion, exactly as you would judge it in English.',
  'When you are uncertain, answer needs_review — never guess low_value.',
  '',
  'Explain in one short sentence, in plain language, citing what in the submission decided it.',
].join('\n');

export function buildFormTriageMessages(
  input: FormTriageInput,
): Array<{ role: 'system' | 'user'; content: string }> {
  const submission = [
    `Name: ${present(input.name)}`,
    `Phone: ${present(input.phone)}`,
    `Email: ${present(input.email)}`,
    `Message: ${present(input.message)}`,
  ].join('\n');
  return [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'user', content: `Form submission:\n${submission}` },
  ];
}

/**
 * Coerce a model response into a storable result. Anything unrecognized becomes
 * Needs Review rather than a rejected write, so a Form Entry is never left
 * without a classification.
 */
export function normalizeFormTriageResult(
  value: unknown,
  classifiedAt: string = new Date().toISOString(),
): FormTriageResult {
  const record = isRecord(value) ? value : {};
  const classification = formTriageClassification(record.classification);
  const explanation = typeof record.explanation === 'string' ? record.explanation.trim() : '';
  if (!classification) {
    return {
      classification: FORM_TRIAGE_FALLBACK,
      explanation: explanation.slice(0, FORM_TRIAGE_MAX_EXPLANATION_CHARS)
        || 'Automatic classification returned no usable answer, so this entry is held for review.',
      classifiedAt,
    };
  }
  return {
    classification,
    explanation: explanation.slice(0, FORM_TRIAGE_MAX_EXPLANATION_CHARS)
      || 'No explanation was returned with this classification.',
    classifiedAt,
  };
}

/** The fallback written when classification could not run at all. */
export function unavailableFormTriageResult(
  reason?: string,
  classifiedAt: string = new Date().toISOString(),
): FormTriageResult {
  return {
    classification: FORM_TRIAGE_FALLBACK,
    explanation: reason
      ? `Automatic classification was unavailable (${reason.slice(0, 200)}), so this entry is held for review.`
      : 'Automatic classification was unavailable, so this entry is held for review.',
    classifiedAt,
  };
}

/** The stored columns for a result — one shape for insert, backfill, and correction. */
export function formTriageColumns(result: FormTriageResult): {
  triage_classification: FormTriageClassification;
  triage_explanation: string;
  triage_classified_at: string;
} {
  return {
    triage_classification: result.classification,
    triage_explanation: result.explanation,
    triage_classified_at: result.classifiedAt,
  };
}

function present(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '(not provided)';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
